(function () {
  const HIDDEN_PRICING_KEY = '_omalley_pricing';
  const HIDDEN_PCT_KEY = '_omalley_pct';
  const VISIBLE_PRICING_KEY = 'omalley_pricing';
  const VISIBLE_PCT_KEY = 'omalley_pct';

  function readConfig(form) {
    const node =
      form.querySelector('[data-omalley-product-pricing-config]') ||
      document.querySelector('[data-omalley-product-pricing-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch {
      return null;
    }
  }

  function findSectionId(form) {
    const productForm = form.closest('product-form');
    if (productForm?.dataset?.sectionId) {
      return productForm.dataset.sectionId;
    }

    const qtyInput = findQuantityInput(form);
    return qtyInput?.closest('quantity-input')?.dataset?.section || null;
  }

  function findQuantityInput(form) {
    if (form.id) {
      const linkedInput = document.querySelector(`input[name="quantity"][form="${CSS.escape(form.id)}"]`);
      if (linkedInput) return linkedInput;
    }

    const sectionId = form.closest('product-form')?.dataset?.sectionId;
    if (sectionId) {
      return document.getElementById(`Quantity-${sectionId}`);
    }

    return form.querySelector('[name="quantity"]');
  }

  function findPriceRoot(form) {
    const sectionId = findSectionId(form);
    if (sectionId) {
      return document.getElementById(`price-${sectionId}`);
    }
    return document.querySelector('[id^="price-"]');
  }

  function tierUnitPrice(baseCents, percentOff) {
    if (!percentOff) return baseCents;
    return Math.round((baseCents * (100 - percentOff)) / 100);
  }

  function computeUnitPrice(config, unitRetailCents, quantity) {
    if (!config || !unitRetailCents || quantity <= 0) return unitRetailCents;

    let volumeUnit = unitRetailCents;
    if (config.volumeEnabled && Array.isArray(config.tiers) && config.tiers.length) {
      const tiers = [...config.tiers].sort(
        (a, b) => (b.minimumQuantity || 0) - (a.minimumQuantity || 0),
      );
      for (const tier of tiers) {
        const minQty = Number(tier.minimumQuantity || 0);
        if (quantity >= minQty) {
          volumeUnit = tierUnitPrice(unitRetailCents, Number(tier.percentOff || 0));
          break;
        }
      }
    }

    if (config.isWholesaleCustomer && config.wholesaleEnabled) {
      return Math.round((volumeUnit * (config.wholesaleMultiplier ?? 85)) / 100);
    }

    return volumeUnit;
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      const format =
        window.omalleyMoneyFormat ||
        (typeof theme !== 'undefined' && theme.moneyFormat) ||
        '${{amount}}';
      return Shopify.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function updateProductPriceDisplay(form) {
    const config = readConfig(form);
    if (!config) return;

    const qtyInput = findQuantityInput(form);
    const quantity = Math.max(1, Number(qtyInput?.value || 1));
    const variantPrice = Number(form.dataset.omalleyVariantPriceCents || config.variantPriceCents);
    const displayUnit = computeUnitPrice(config, variantPrice, quantity);
    const lineTotal = displayUnit * quantity;
    const priceRoot = findPriceRoot(form);
    if (!priceRoot) return;

    const wholesaleEl = priceRoot.querySelector('[data-omalley-live-wholesale-price]');
    const retailEl = priceRoot.querySelector('[data-omalley-live-retail-price]');
    const perItemEl = priceRoot.querySelector('[data-omalley-live-unit-price]');
    const showVolume = config.volumeEnabled && displayUnit < variantPrice;

    if (wholesaleEl) {
      wholesaleEl.textContent = formatMoney(displayUnit);
    }

    if (retailEl && config.volumeEnabled) {
      retailEl.textContent =
        quantity > 1 ? formatMoney(lineTotal) : formatMoney(displayUnit);
    }

    if (perItemEl) {
      if (showVolume || (config.isWholesaleCustomer && config.wholesaleEnabled)) {
        perItemEl.hidden = false;
        if (quantity > 1) {
          perItemEl.textContent =
            formatMoney(displayUnit) + ' each · ' + quantity + ' items';
        } else {
          perItemEl.textContent = formatMoney(displayUnit) + ' each at this quantity';
        }
      } else {
        perItemEl.hidden = true;
      }
    }
  }

  function computePricing(config, unitRetailCents, quantity) {
    if (!config || !unitRetailCents || quantity <= 0) return null;

    const targetUnit = computeUnitPrice(config, unitRetailCents, quantity);
    let label = 'Volume pricing';
    if (config.isWholesaleCustomer && config.wholesaleEnabled) {
      label =
        config.volumeEnabled && targetUnit < unitRetailCents
          ? 'Wholesale volume pricing'
          : 'Wholesale pricing';
    }

    if (targetUnit >= unitRetailCents) return null;

    const pct = ((unitRetailCents - targetUnit) / unitRetailCents) * 100;
    if (!Number.isFinite(pct) || pct <= 0) return null;

    return {
      pct: pct.toFixed(2),
      label,
    };
  }

  function upsertPropertyInput(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      form.appendChild(input);
    }
    input.name = name;
    input.value = value;
    input.disabled = false;
  }

  function clearPropertyInputs(form) {
    form
      .querySelectorAll(
        `input[name="properties[${HIDDEN_PRICING_KEY}]"], input[name="properties[${VISIBLE_PRICING_KEY}]"], input[name="properties[${HIDDEN_PCT_KEY}]"], input[name="properties[${VISIBLE_PCT_KEY}]"]`,
      )
      .forEach((input) => {
        input.value = '';
        input.disabled = true;
      });
  }

  function applyPricingProperties(form) {
    const config = readConfig(form);
    if (!config) return;

    const qtyInput = findQuantityInput(form);
    const quantity = Number(qtyInput?.value || 1);
    const variantPrice = Number(form.dataset.omalleyVariantPriceCents || config.variantPriceCents);
    const pricing = computePricing(config, variantPrice, quantity);
    if (!pricing) {
      clearPropertyInputs(form);
      return;
    }

    const payload = JSON.stringify({ pct: pricing.pct, label: pricing.label });
    upsertPropertyInput(form, `properties[${HIDDEN_PCT_KEY}]`, pricing.pct);
    upsertPropertyInput(form, `properties[${HIDDEN_PRICING_KEY}]`, payload);
  }

  function refreshProductPricing(form) {
    applyPricingProperties(form);
    updateProductPriceDisplay(form);
  }

  function bindVariantUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data?.variant?.price) return;
      document.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
        form.dataset.omalleyVariantPriceCents = String(data.variant.price);
        refreshProductPricing(form);
      });
    });
  }

  function bindForms() {
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
        applyPricingProperties(form);
      },
      true,
    );

    document.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
      refreshProductPricing(form);
    });
  }

  function formForQuantityInput(target) {
    if (!(target instanceof HTMLInputElement) || target.name !== 'quantity') return null;

    const formId = target.getAttribute('form');
    const form = formId
      ? document.getElementById(formId)
      : target.closest('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return null;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return null;
    return form;
  }

  function bindQuantityListeners() {
    document.addEventListener('change', (event) => {
      const form = formForQuantityInput(event.target);
      if (form) refreshProductPricing(form);
    });

    document.addEventListener('input', (event) => {
      const form = formForQuantityInput(event.target);
      if (form) refreshProductPricing(form);
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest('.quantity__button');
      if (!button) return;

      const qtyInput = button.closest('quantity-input')?.querySelector('input[name="quantity"]');
      const form = qtyInput ? formForQuantityInput(qtyInput) : null;
      if (!form) return;

      window.setTimeout(() => refreshProductPricing(form), 0);
    });
  }

  function init() {
    bindVariantUpdates();
    bindForms();
    bindQuantityListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
